import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { salesModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { createColumnHelper } from "@tanstack/react-table";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import {
  listSalesShipments,
  type SalesShipmentListRow,
} from "@/shared/api/sales";
import { queryKeys } from "@/shared/query/keys";

const PAGE_SIZE = 50;
const columnHelper = createColumnHelper<DeskTableFeatures, SalesShipmentListRow>();

export function SalesShipmentsPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const { searchParams, patchParams } = useListUrlParams();
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.salesShipments.list(companySlug ?? "", {
      q: debouncedQ,
      limit: PAGE_SIZE,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listSalesShipments(companySlug!, {
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  useEffect(() => {
    const msg =
      listQuery.error instanceof Error ? listQuery.error.message : null;
    if (msg === "Not signed in") navigate("/login", { replace: true });
  }, [listQuery.error, navigate]);

  const rows = listQuery.data?.pages.flatMap((p) => p.shipments) ?? [];
  const filtersActive = Boolean(query.trim());
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });

  if (!companySlug) return null;

  const columns = columnHelper.columns([
    columnHelper.accessor("number", {
      header: "Number",
      size: 120,
      cell: ({ row }) => (
        <DocNumberLink
          to={`/c/${companySlug}/sales/in-transit/${row.original.id}`}
        >
          {row.original.number}
        </DocNumberLink>
      ),
    }),
    columnHelper.accessor("lifecycle", {
      header: "Status",
      size: 100,
      cell: ({ getValue }) => <LifecycleStatus value={getValue()} showLabel />,
    }),
    columnHelper.accessor("customerName", {
      header: "Customer",
      size: 180,
    }),
    columnHelper.accessor("salesOrderNumber", {
      header: "Order",
      size: 120,
      cell: ({ row }) => (
        <Link
          to={`/c/${companySlug}/sales/orders/${row.original.salesOrderId}`}
          className="font-mono text-sm no-underline"
        >
          {row.original.salesOrderNumber}
        </Link>
      ),
    }),
    columnHelper.accessor("docDate", {
      header: "Date",
      size: 110,
    }),
  ]);

  return (
    <PageFrame>
      <DataTableChrome>
        <DeskListFilterReset {...filterReset} />
        <DeskListHeader
          title="In transit"
          description="Optional shipments before delivery — skip anytime and deliver from the order."
          tabs={
            <DeskSubNav
              placement="header"
              items={salesModuleTabs(companySlug)}
            />
          }
          primaryAction={
            <Button asChild size="sm" variant="outline">
              <Link
                to={`/c/${companySlug}/sales/orders`}
                className="no-underline"
              >
                From order
              </Link>
            </Button>
          }
        />
        <AdminFilters>
          <Input
            className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
            placeholder="Search number or customer…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </AdminFilters>
        {listQuery.isPending ? (
          <Alert variant="muted" className="mt-8">
            Loading…
          </Alert>
        ) : null}
        {!listQuery.isPending && rows.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title={debouncedQ ? `No match for “${debouncedQ}”` : "No shipments yet"}
              description="Create In transit from an open sales order (Create ▾), or deliver directly."
            />
          </div>
        ) : null}
        {rows.length > 0 ? (
          <DataTable
            listId="sales-in-transit"
            companySlug={companySlug}
            columns={columns}
            data={rows}
            getRowId={(r) => r.id}
            onRefresh={() => void listQuery.refetch()}
          />
        ) : null}
        {listQuery.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              disabled={listQuery.isFetchingNextPage}
              pending={listQuery.isFetchingNextPage}
              pendingLabel="Loading more…"
              onClick={() => void listQuery.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </DataTableChrome>
    </PageFrame>
  );
}
